import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
    collection,
    doc,
    setDoc,
    deleteDoc,
    onSnapshot,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const PRODUCTS_COLLECTION = 'products';

const ProductsContext = createContext();

export const useProducts = () => {
    const context = useContext(ProductsContext);
    if (!context) {
        throw new Error('useProducts must be used within a ProductsProvider');
    }
    return context;
};

export const ProductsProvider = ({ children }) => {
    // Products grouped by category, e.g. { women: [...], men: [...] }
    const [productsByCategory, setProductsByCategory] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, PRODUCTS_COLLECTION),
            (snapshot) => {
                const grouped = {};
                snapshot.forEach((docSnap) => {
                    const data = { ...docSnap.data(), productId: docSnap.id };
                    const category = (data.category || 'others').toLowerCase();
                    if (!grouped[category]) grouped[category] = [];
                    grouped[category].push(data);
                });
                setProductsByCategory(grouped);
                setLoading(false);
            },
            (err) => {
                console.error('Failed to load products:', err);
                setError(err.message);
                setLoading(false);
            }
        );

        return unsubscribe;
    }, []);

    const getAllProducts = useCallback(
        () => Object.values(productsByCategory).flat(),
        [productsByCategory]
    );

    const getProductsByCategory = useCallback(
        (category) => productsByCategory[(category || '').toLowerCase()] || [],
        [productsByCategory]
    );

    const getProductById = useCallback(
        (productId) => getAllProducts().find((p) => p.productId === productId),
        [getAllProducts]
    );

    const getRecommendedProducts = useCallback(
        (limit = 10) => {
            return [...getAllProducts()]
                .sort((a, b) => (b.inStock ? 1 : 0) - (a.inStock ? 1 : 0))
                .slice(0, limit);
        },
        [getAllProducts]
    );

    const searchProducts = useCallback(
        (query) => {
            const lowerQuery = (query || '').toLowerCase();
            return getAllProducts().filter(
                (product) =>
                    product.productTitle?.toLowerCase().includes(lowerQuery) ||
                    product.productDesc?.toLowerCase().includes(lowerQuery) ||
                    product.category?.toLowerCase().includes(lowerQuery) ||
                    product.sku?.toLowerCase().includes(lowerQuery)
            );
        },
        [getAllProducts]
    );

    // Creates or overwrites a product document (productId is used as the document id)
    const saveProduct = useCallback(async (productId, productData) => {
        const productRef = doc(db, PRODUCTS_COLLECTION, productId);
        await setDoc(
            productRef,
            { ...productData, category: (productData.category || 'others').toLowerCase(), updatedAt: serverTimestamp() },
            { merge: true }
        );
    }, []);

    const deleteProduct = useCallback(async (productId) => {
        await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
    }, []);

    const value = useMemo(
        () => ({
            loading,
            error,
            getAllProducts,
            getProductsByCategory,
            getProductById,
            getRecommendedProducts,
            searchProducts,
            saveProduct,
            deleteProduct
        }),
        [loading, error, getAllProducts, getProductsByCategory, getProductById, getRecommendedProducts, searchProducts, saveProduct, deleteProduct]
    );

    return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};
